/** Registro de preço com campos mínimos para resolução de vigência. */
export type PrecoRegistro = {
  id: string
  produto_id: string
  valor_unitario: number
  vigencia_inicio: string
  criado_em?: string | null
}

/** Ordem cronológica de inserção (mais antigo → mais recente). */
export function sortPrecosByInsercao<T extends { criado_em?: string | null; id: string }>(
  precos: T[],
): T[] {
  return [...precos].sort((a, b) => {
    const ta = a.criado_em || ''
    const tb = b.criado_em || ''
    if (ta !== tb) return ta.localeCompare(tb)
    return a.id.localeCompare(b.id)
  })
}

/**
 * Preço válido em `refDate`: maior vigência ≤ refDate; em empate de vigência, última inserção.
 */
export function resolvePrecoVigente<T extends PrecoRegistro>(precos: T[], refDate: string): T | null {
  const eligible = precos.filter((p) => p.vigencia_inicio <= refDate)
  if (!eligible.length) return null

  const maxVigencia = eligible.reduce(
    (max, p) => (p.vigencia_inicio > max ? p.vigencia_inicio : max),
    eligible[0].vigencia_inicio,
  )
  const sameVigencia = eligible.filter((p) => p.vigencia_inicio === maxVigencia)
  return sortPrecosByInsercao(sameVigencia).at(-1) ?? null
}

/** Preço vigente do produto comercial vinculado ao alimento. */
export function resolvePrecoVigentePorProduto(
  precos: PrecoRegistro[],
  produtoId: string | null | undefined,
  refDate: string,
): PrecoRegistro | null {
  if (!produtoId) return null
  return resolvePrecoVigente(
    precos.filter((p) => p.produto_id === produtoId),
    refDate,
  )
}

export type PrecoGrupo<T extends PrecoRegistro> = {
  produtoId: string
  produtoNome: string
  valido: T | null
  /** Demais registros, em ordem de inserção (sem o válido atual). */
  historico: T[]
}

export function groupPrecosPorProduto<T extends PrecoRegistro & { produto?: { nome: string } | null }>(
  precos: T[],
  refDate: string,
): PrecoGrupo<T>[] {
  const byProduto = new Map<string, T[]>()
  for (const p of precos) {
    const list = byProduto.get(p.produto_id) ?? []
    list.push(p)
    byProduto.set(p.produto_id, list)
  }

  return [...byProduto.entries()].map(([produtoId, rows]) => {
    const valido = resolvePrecoVigente(rows, refDate)
    const historico = sortPrecosByInsercao(rows).filter((r) => r.id !== valido?.id)
    return {
      produtoId,
      produtoNome: rows[0]?.produto?.nome ?? '',
      valido,
      historico,
    }
  })
}

/** @deprecated use groupPrecosPorProduto */
export type PrecoGrupoAlimento<T extends PrecoRegistro> = {
  alimentoId: string
  alimentoNome: string
  valido: T | null
  historico: T[]
}

/** @deprecated use groupPrecosPorProduto */
export function groupPrecosPorAlimento<T extends PrecoRegistro & { alimento?: { nome: string } | null }>(
  precos: T[],
  refDate: string,
): PrecoGrupoAlimento<T>[] {
  return groupPrecosPorProduto(
    precos.map((p) => ({
      ...p,
      produto_id: (p as { alimento_id?: string }).alimento_id ?? p.produto_id,
      produto: p.alimento ? { nome: p.alimento.nome } : null,
    })),
    refDate,
  ).map((g) => ({
    alimentoId: g.produtoId,
    alimentoNome: g.produtoNome,
    valido: g.valido,
    historico: g.historico,
  }))
}
